import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DataTable } from '@/components/ui/data-table'
import { Input } from '@/components/ui/input'
import { ColumnDef } from '@tanstack/react-table'
import OrderCell from 'app/frontend/base/components/OrderCell'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import { questionTypes } from 'app/frontend/types/custom/QuestionType'
import { Plus, Trash2, Undo2 } from 'lucide-react'
import { useMemo } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const name = 'questions_attributes'

export type EditableQuestion = wowaudit.Question & {
  _destroy?: boolean
}

const QuestionsTable = () => {
  const { t } = useTranslation()
  const { watch, setValue, register } = useFormContext()
  const { append, remove } = useFieldArray({ name })

  const questions: EditableQuestion[] = watch(name) ?? []

  const columns: ColumnDef<EditableQuestion>[] = useMemo(
    () => [
      {
        id: 'question_type',
        header: 'Type',
        size: 145,
        cell: (info) => {
          return (
            <SelectField
              layout='stacked'
              name={`${name}.${info.row.index}.questionType`}
              options={[...questionTypes]}
              disabled={!!info.row.original._destroy}
              className={info.row.original._destroy ? 'opacity-30' : ''}
              customLabel=''
            />
          )
        },
      },
      {
        id: 'description',
        header: 'Description',
        cell: (info) => {
          const isDropdown = ['dropdown', 'radio'].includes(
            watch(`${name}.${info.row.index}.questionType`),
          )
          const optionsPath = `${name}.${info.row.index}.question_options_attributes`
          const options =
            (watch(optionsPath) as Array<
              (wowaudit.QuestionOption & { _destroy?: boolean }) | undefined
            >) ?? []
          const rowDisabled = !!info.row.original._destroy

          return (
            <div>
              <Input
                {...register(`${name}.${info.row.index}.description`)}
                disabled={rowDisabled}
                required={true}
                className={rowDisabled ? 'opacity-30' : ''}
              />

              {isDropdown && (
                <div
                  className={`mt-2 space-y-2 ${
                    rowDisabled ? 'opacity-30' : ''
                  }`}
                >
                  {options?.map((opt, i) => {
                    if (!opt) return null
                    const optionField = `${optionsPath}.${i}.option`
                    const destroyField = `${optionsPath}.${i}._destroy`
                    const disabled = rowDisabled
                    return (
                      <div key={i} className='flex items-center gap-2'>
                        <div className='text-xs text-muted-foreground w-20'>
                          {`Option ${i + 1}:`}
                        </div>
                        <Input
                          {...register(optionField)}
                          disabled={disabled || !!opt._destroy}
                          className={opt._destroy ? 'opacity-30' : ''}
                        />
                        <Button
                          type='button'
                          size='sm'
                          variant={opt._destroy ? 'secondary' : 'destructive'}
                          onClick={() =>
                            opt.id
                              ? setValue(destroyField, !opt._destroy)
                              : setValue(
                                  optionsPath,
                                  [
                                    ...options.slice(0, i),
                                    ...options.slice(i + 1),
                                  ].filter((v) => v !== undefined),
                                )
                          }
                          className='shrink-0'
                          disabled={rowDisabled}
                        >
                          {opt._destroy ? (
                            <Undo2 size={14} />
                          ) : (
                            <Trash2 size={14} />
                          )}
                        </Button>
                      </div>
                    )
                  })}

                  <div className='pt-1'>
                    <Button
                      type='button'
                      size='sm'
                      variant='outline'
                      onClick={() =>
                        setValue(optionsPath, [
                          ...(options ?? []),
                          { option: '' },
                        ])
                      }
                      disabled={rowDisabled}
                    >
                      <Plus size={14} className='mr-1' />
                      Add option
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )
        },
      },
      {
        id: 'required',
        header: 'Req',
        size: 45,
        cell: (info) => {
          const isRequired = watch(`${name}.${info.row.index}.required`)
          return (
            <div
              className={`flex justify-center mt-3 ${
                info.row.original._destroy ? 'opacity-30' : ''
              }`}
            >
              <Checkbox
                checked={isRequired}
                disabled={!!info.row.original._destroy}
                onCheckedChange={(checked) =>
                  setValue(`${name}.${info.row.index}.required`, checked)
                }
              />
            </div>
          )
        },
      },
      {
        id: 'order',
        header: 'Order',
        size: 90,
        cell: (info) => {
          if (info.row.original._destroy) {
            return <div className='opacity-30 h-8' />
          }

          const activeQuestions = questions.filter((q) => !q._destroy)
          const activeIndex = activeQuestions.findIndex(
            (q) => q === questions[info.row.index],
          )

          if (activeIndex === -1) return null

          return (
            <OrderCell
              name={name}
              index={info.row.index}
              totalLength={questions.length}
            />
          )
        },
      },
      {
        id: 'actions',
        header: '',
        size: 50,
        cell: (info) => {
          return (
            <div className='flex justify-end mt-0.5'>
              <Button
                type='button'
                size='sm'
                variant={
                  info.row.original._destroy ? 'secondary' : 'destructive'
                }
                onClick={() =>
                  info.row.original.id
                    ? setValue(
                        `${name}.${info.row.index}._destroy`,
                        !info.row.original._destroy,
                      )
                    : remove(info.row.index)
                }
              >
                {info.row.original._destroy ? (
                  <Undo2 size={14} />
                ) : (
                  <Trash2 size={14} />
                )}
              </Button>
            </div>
          )
        },
      },
    ],
    [questions.length],
  )

  return (
    <div className='mb-6'>
      <DataTable
        className='bg-background'
        cellClassName='align-top px-2'
        includeHeader={questions && questions.length > 0}
        columns={columns}
        data={questions}
        emptyMessage={() => t('settings.team.questions.empty-message')}
      />

      <div className='flex justify-end'>
        <Button
          type='button'
          size='sm'
          className='mt-2'
          variant='outline'
          onClick={() =>
            append({
              questionType: 'text',
              description: '',
              required: false,
              order: questions.length,
            })
          }
        >
          <Plus size={14} className='mr-1' />
          {t('settings.team.questions.add-question')}
        </Button>
      </div>
    </div>
  )
}

export default QuestionsTable
